// La Ayuda: qué hace cada pantalla y cómo decide la app.
//
// ═══════════════════════════════════════════════════════════════════
// ESTA PANTALLA TIENE UN GUARDIÁN, Y ES LO QUE LA MANTIENE VIVA
// ═══════════════════════════════════════════════════════════════════
//
// Una ayuda que no se actualiza es peor que no tener ayuda: describe una
// app que ya no existe y le enseña al usuario a no leerla. Por eso cada
// sección declara, además de sus textos, DE QUÉ ARCHIVO habla y qué
// ANCLAS documenta — claves t() reales de esa pantalla, las mismas que
// dibujan sus botones.
//
// `test_ayuda.js` se pone rojo si:
//
//   · una pestaña de la navegación (App.js) no tiene sección acá;
//   · una sección apunta a un archivo que ya no existe;
//   · un ancla documentada desapareció de su pantalla (la función se
//     sacó o se renombró y la Ayuda sigue hablando de ella);
//   · los números que la Ayuda dice sobre el motor —los cortes de
//     frescura y de uso, cuántas propuestas se muestran— dejaron de
//     coincidir con las constantes de lib/combinaciones.js y
//     lib/rutas_outfits.js.
//
// La regla de trabajo, escrita en la memoria del proyecto: TODA
// FUNCIONALIDAD NUEVA O CAMBIADA ACTUALIZA ESTA PANTALLA, en los doce
// idiomas, con sus anclas. Los textos van con t(): el español viaja
// como respaldo acá y los otros once viven en src/idiomas/.
//
// Se abre desde la cabecera como un modal, igual que el perfil, y a
// propósito: consultar la ayuda desde el medio de una tarea no tiene
// que sacarte de la tarea. "Perder el lugar es lo que hace que uno no
// se moleste" vale también para leer.

import { useEffect } from "react";
import { t, useIdioma } from "./i18n";
import { useFocoAtrapado } from "./foco";

/* ⚠️  ESTOS DOS NÚMEROS SON COPIA DE lib/plan.js, Y LA COPIA ESTÁ
 *     VIGILADA.
 *
 * El servidor no le manda estos valores a esta pantalla —la Ayuda no
 * pide nada a la API, se abre y se lee— así que hay que escribirlos.
 * Una Ayuda que dice "hasta 25 prendas" mientras el servidor corta en
 * 40 no da error: le miente al usuario, con confianza, para siempre.
 *
 * Por eso `test_plan.js` §5 lee los dos archivos y compara los números.
 * Si alguien cambia lib/plan.js y se olvida de acá, `npm test` se pone
 * rojo. La duplicación es aceptable; la duplicación sin vigilancia no. */
const TOPE_PRUEBA = 25;
const DIAS_PRUEBA = 60;

/* ⚠️  ESTA TABLA ES DATOS, NO DECORACIÓN. `test_ayuda.js` la lee con una
 *     expresión regular, así que el formato importa: un objeto por
 *     sección, `id` en la primera línea, `archivo` y `anclas` como
 *     literales. Si se reestructura, hay que reestructurar el test.
 *
 * `id`      — coincide con la pestaña de App.js (`setPestana("…")`), o
 *             es una de las tres que no son pestaña: inicio, perfil,
 *             ficha.
 * `archivo` — la pantalla que documenta, en src/.
 * `anclas`  — claves t() de ESA pantalla que la sección menciona. Si
 *             una desaparece del archivo, la Ayuda está describiendo
 *             algo que ya no existe. */
export const SECCIONES = [
  {
    id: "inicio",
    archivo: "PrimerUso.js",
    anclas: ["inicio.dondeVives", "inicio.queRopa", "inicio.comoEsTuDia", "inicio.conCualTeVes"],
  },
  {
    id: "outfits",
    archivo: "Outfits.js",
    anclas: [
      "outfits.vestirsePara",
      "outfits.ocasion",
      "outfits.sinMostrar",
      "outfits.combinarPrenda",
      "outfits.otrasOpciones",
      "outfits.editar",
      "outfits.noVa",
      "outfits.meLoPuse",
      "outfits.verTodasIgual",
      "outfits.activarReglas",
      "outfits.paraSumar",
    ],
  },
  {
    id: "armar",
    archivo: "Armador.js",
    anclas: ["armador.buscarPorNombre", "armador.registrar", "armador.meLoPuseHoy", "armador.otraFecha"],
  },
  {
    id: "armario",
    archivo: "Armario.js",
    anclas: [
      "armario.buscar",
      "armario.filtros",
      "armario.orden",
      "armario.marca",
      "armario.fichada",
      "armario.editar",
      "armario.borrar",
      "armario.deshacer",
      "armario.cambiarFoto",
      "armario.sinCombis",
      "armario.complemento",
    ],
  },
  {
    id: "ficha",
    archivo: "EditarPrenda.js",
    anclas: [
      "editar.disponibilidad",
      "editar.estado",
      "editar.ocasiones",
      "editar.colores",
      "editar.cambiar",
      "editar.estaciones",
      "editar.marca",
      "editar.talla",
      "editar.masNotas",
      "editar.usosAbrir",
      "ficha.fichada",
    ],
  },
  {
    id: "perfumes",
    archivo: "Perfumes.js",
    anclas: [
      "perfumes.agregar",
      "perfumes.completarIA",
      "perfumes.nivelEtiqueta",
      "perfumes.abiertoEn",
      "perfumes.seTermina",
      "perfumes.ponerFoto",
    ],
  },
  {
    id: "historial",
    archivo: "Historial.js",
    anclas: [
      "historial.vistaLista",
      "historial.vistaCalendario",
      "historial.fichar",
      "historial.borrarRegistro",
      "historial.rotoOtro",
    ],
  },
  {
    id: "valija",
    archivo: "Valija.js",
    anclas: [
      "valija.cuantosDias",
      "valija.estacion",
      "valija.calzados",
      "valija.quieresLlevar",
      "valija.ahorro",
      "valija.verPlan",
      "valija.nueva",
    ],
  },
  {
    id: "comprar",
    archivo: "Comprar.js",
    anclas: [
      "comprar.sinOutfit",
      "comprar.huerfanasTitulo",
      "comprar.dependeTitulo",
      "comprar.gastadasTitulo",
      "comprar.queConviene",
      "comprar.comoSeCalcula",
    ],
  },
  {
    id: "cargar",
    archivo: "CargaLote.js",
    anclas: [
      "carga.arrastra",
      "carga.tomarFoto",
      "carga.quitarTodas",
      "carga.hastaPorTanda",
      "carga.iaDudosa",
      "carga.yaEnArmario",
      "carga.cambiarColores",
      "carga.guardarPrendas",
      "carga.recienGuardadas",
      "carga.recuperadas",
    ],
  },
  {
    id: "perfil",
    archivo: "Perfil.js",
    anclas: [
      "perfil.comoSugiero",
      "perfil.coloresNoUsas",
      "perfil.dondeVives",
      "perfil.tuEstilo",
      "perfil.acentos",
      "perfil.misListas",
      "perfil.idioma",
    ],
  },
];

export default function Ayuda({ onCerrar }) {
  // Ver src/foco.js (BA-14).
  const caja = useFocoAtrapado();
  useIdioma();

  // Igual que el modal del perfil: Escape cierra y la página de atrás
  // no scrollea mientras esto está abierto.
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const alTeclear = (e) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", alTeclear);
    return () => {
      document.body.style.overflow = previo;
      window.removeEventListener("keydown", alTeclear);
    };
  }, [onCerrar]);

  const irA = (id) => {
    const el = document.getElementById(`ayuda-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const titulos = {
    inicio: t("ayuda.inicioTitulo", "Empezar"),
    outfits: t("ayuda.outfitsTitulo", "Qué me pongo"),
    armar: t("ayuda.armarTitulo", "Armar a mano"),
    armario: t("ayuda.armarioTitulo", "Mi armario"),
    ficha: t("ayuda.fichaTitulo", "La ficha de una prenda"),
    perfumes: t("ayuda.perfumesTitulo", "Perfumes"),
    historial: t("ayuda.historialTitulo", "Historial y calendario"),
    valija: t("ayuda.valijaTitulo", "Valija"),
    comprar: t("ayuda.comprarTitulo", "Qué comprar"),
    cargar: t("ayuda.cargarTitulo", "Cargar prendas"),
    perfil: t("ayuda.perfilTitulo", "Tu perfil"),
  };

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label={t("ayuda.titulo", "Ayuda")}
      onClick={onCerrar}
    >
      <div className="modal-caja" ref={caja} onClick={(e) => e.stopPropagation()}>
        <div className="modal-cabecera">
          <h2 className="modal-titulo">{t("ayuda.titulo", "Ayuda")}</h2>
          <button className="modal-cerrar" onClick={onCerrar} aria-label={t("comun.cerrar", "Cerrar")}>
            ×
          </button>
        </div>

        <div className="modal-cuerpo ayuda">
          <p className="tenue">
            {t(
              "ayuda.intro",
              "Qué hace cada pantalla y cómo decide Brujel qué proponerte."
            )}
          </p>

          {/* El índice: un botón por sección, en el orden de las pestañas. */}
          <nav className="opciones-chips ayuda-indice" aria-label={t("ayuda.indice", "Secciones")}>
            {SECCIONES.map((s) => (
              <button key={s.id} type="button" className="chip" onClick={() => irA(s.id)}>
                {titulos[s.id]}
              </button>
            ))}
          </nav>

          {/* ── Empezar ─────────────────────────────────────────── */}
          <section id="ayuda-inicio" className="ayuda-seccion">
            <h3>{titulos.inicio}</h3>
            <p>
              {t(
                "ayuda.inicio1",
                "Brujel te propone qué ponerte con la ropa que ya tienes. Le sacas una foto a cada prenda y la app la reconoce sola: qué es, sus colores, el material, para qué ocasiones sirve, en qué estaciones y la marca si se ve. Con una prenda de arriba, una de abajo y un calzado ya empieza a proponerte conjuntos."
              )}
            </p>
            <p>
              {t(
                "ayuda.inicio2",
                "La primera vez te hace cuatro preguntas. Dónde vives, para saber qué estación es hoy (en agosto es invierno en Buenos Aires y verano en Madrid). Qué ropa usas, para mostrarte primero lo que más usas; nada se esconde. Cómo es tu día, para armar tu lista de ocasiones, que cambias cuando quieras. Y con qué estilo te identificas, para ordenar las propuestas; ninguna se descarta."
              )}
            </p>
          </section>

          {/* ── Qué me pongo ────────────────────────────────────── */}
          <section id="ayuda-outfits" className="ayuda-seccion">
            <h3>{titulos.outfits}</h3>
            <p>
              {t(
                "ayuda.outfits1",
                "Es la pantalla de todos los días. Arriba eliges para qué estación te vistes (empieza en la de hoy según tu país), la ocasión, y si quieres combinar solo ropa que todavía no mostraste en público. Con «Combinar una prenda» eliges una prenda y ves únicamente conjuntos que la incluyen, aunque no cumpla los filtros; la app te dice qué filtro está dejando pasar."
              )}
            </p>
            <p>
              {t(
                "ayuda.outfits2",
                "Antes de combinar, la app separa las prendas que pueden participar: las disponibles (no en el lavadero ni prestadas), las que sirven para la ocasión y la estación elegidas, y deja fuera las gastadas cuando la ocasión lo merece. Si con eso no alcanza para vestirte, vuelve a incluir lo gastado o la ropa de otra temporada, y te lo avisa."
              )}
            </p>
            <p>
              {t(
                "ayuda.outfits3",
                "Cada conjunto tiene algo para arriba, algo para abajo y un calzado (un vestido o enterizo cubre dos). Abrigos y accesorios se ofrecen aparte, para sumar encima, y según la estación cada conjunto trae un abrigo que combina: liviano en primavera y otoño, de verdad en invierno, ninguno en verano. Tres reglas descartan conjuntos: el color (no demasiados colores fuertes juntos; una sola prenda estampada con el resto neutro sí pasa), la formalidad (una camiseta de gimnasio y un saco no van juntos en ninguna ocasión) y tus vetos («esto con esto no»). «Ver todas igual» apaga las reglas de color y formalidad; tus vetos se respetan siempre."
              )}
            </p>
            <p>
              {t(
                "ayuda.outfits4",
                "Las propuestas vienen ordenadas por cuatro criterios, del más importante al que desempata. Primero, hace cuánto que no usas esas prendas: nunca usadas, hace más de un mes, entre una semana y un mes, esta semana. Segundo, los conjuntos con ropa que aún no mostraste en público van antes. Tercero, cuántas veces las usaste: nunca, casi sin estrenar (menos de 3), uso normal (menos de 12), de todos los días. Cuarto, tu estilo: entre conjuntos parejos gana el que más se parece a tu estilo; solo ordena, nunca descarta. Lo que sigue empatado se sortea, y «Otras opciones» vuelve a sortear."
              )}
            </p>
            <p>
              {t(
                "ayuda.outfits5",
                "Ves 18 propuestas por vez, elegidas para no repetirte las mismas prendas y en el orden de arriba. En cada una puedes «Editar» para cambiar una, dos o las tres prendas; marcar «No combina» y decir qué dos prendas no van juntas (queda como veto); o registrar «Me lo puse» hoy, ayer o anteayer. Registrar lo que te pones alimenta tu historial y hace que las propuestas prioricen lo que hace más tiempo no usas."
              )}
            </p>
          </section>

          {/* ── Armar a mano ────────────────────────────────────── */}
          <section id="ayuda-armar" className="ayuda-seccion">
            <h3>{titulos.armar}</h3>
            <p>
              {t(
                "ayuda.armar1",
                "Para registrar un conjunto que armaste tú, sin que la app lo proponga. Busca las prendas por nombre, súmalas y registra con «Me lo puse hoy», «Ayer» u otra fecha. Al registrar, el armario y el historial se actualizan y pasas al historial a ver el resultado."
              )}
            </p>
          </section>

          {/* ── Mi armario ──────────────────────────────────────── */}
          <section id="ayuda-armario" className="ayuda-seccion">
            <h3>{titulos.armario}</h3>
            <p>
              {t(
                "ayuda.armario1",
                "Todas tus prendas. Puedes buscar por nombre, notas, color, material, tipo, ocasión o marca, y filtrar por estado, disponibilidad, color, ocasión, estación, tipo, subcategoría, marca, sin foto, sin usar todavía, o si ya la mostraste en público. Cada filtro muestra cuántas prendas tiene; si no tiene ninguna, no aparece. Arriba puedes cambiar el orden."
              )}
            </p>
            <p>
              {t(
                "ayuda.armario2",
                "Cada tarjeta muestra la marca si se conoce, cuándo la usaste por última vez, cuántas veces y en cuántos conjuntos entra. «No entra en ninguna combinación» es la señal más útil: esa prenda no está trabajando. Un abrigo o accesorio dice «Se suma encima de cualquier conjunto» porque no ocupa el lugar de otra prenda. Desde la tarjeta cambias la foto, abres la ficha para editar o borras; si borras por error, lo deshaces en el momento."
              )}
            </p>
          </section>

          {/* ── La ficha ────────────────────────────────────────── */}
          <section id="ayuda-ficha" className="ayuda-seccion">
            <h3>{titulos.ficha}</h3>
            <p>
              {t(
                "ayuda.ficha1",
                "Los campos están ordenados por lo que más cambia: primero disponibilidad y estado (mandar algo al lavadero es lo más común), después ocasiones, colores, tipo y qué es exactamente, estaciones, nombre, material, talla, marca y notas. Colores y ocasiones muestran solo lo elegido: un toque sobre uno lo quita, y «Cambiar» abre la lista completa para agregar."
              )}
            </p>
            <p>
              {t(
                "ayuda.ficha2",
                "El nombre que le pusiste nunca se pisa. La marca la propone la app leyendo el logo en la foto y tú la confirmas; si escribes «tommy h» y ya tienes «Tommy Hilfiger», se agrupan solas. «Prenda fichada» marca que ya la mostraste en público y se guarda al instante, sin cerrar la ficha. Solo se guarda lo que cambiaste."
              )}
            </p>
            <p>
              {t(
                "ayuda.ficha3",
                "Abajo de todo, «Cuándo la usaste» abre la lista de fechas en que esa prenda salió a la calle, con la ocasión de cada vez. Es solo para mirar: si te equivocaste al registrar un día, se corrige desde Historial."
              )}
            </p>
          </section>

          {/* ── Historial ───────────────────────────────────────── */}
          {/* ── Perfumes ────────────────────────────────────────── */}
          <section id="ayuda-perfumes" className="ayuda-seccion">
            <h3>{titulos.perfumes}</h3>
            <p>
              {t(
                "ayuda.perfumes1",
                "En Mi armario, arriba, cambias entre Prendas y Perfumes. Para agregar un perfume escribes su nombre y tocas «Completar con IA»: propone la marca, la concentración (colonia, eau de toilette, eau de parfum o parfum), la familia olfativa y si es de día, de noche o para ambos. Lo que propuso se ve marcado hasta que lo revisas. La IA contesta con lo que sabe, sin buscar en internet: si no reconoce el perfume te lo dice y completas los datos a mano. Nunca cambia lo que ya escribiste."
              )}
            </p>
            <p>
              {t(
                "ayuda.perfumes2",
                "En cada tarjeta mueves el deslizador para decir, a ojo, cuánto queda en el frasco. Con la fecha de apertura te avisa a los tres años, cuando un perfume puede empezar a cambiar, y a los cinco. La foto es la tuya, del frasco. Desde la tarjeta también lo editas o lo borras; si lo borras por error, lo deshaces en el momento. Los registros viejos siguen mostrando ese perfume."
              )}
            </p>
            <p>
              {t(
                "ayuda.perfumes3",
                "Cuando tocas «Me lo puse», en Qué me pongo o en Armar a mano, aparece un frasco con el último perfume que usaste (o el último que cargaste): tócalo para cambiarlo o elegir «Sin perfume». Cada día del Historial muestra el conjunto y el frasco, y desde «Editar» puedes corregirlo. Cuando a un perfume le queda 15% o menos, Qué comprar te avisa que se te está terminando."
              )}
            </p>
          </section>

          <section id="ayuda-historial" className="ayuda-seccion">
            <h3>{titulos.historial}</h3>
            <p>
              {t(
                "ayuda.historial1",
                "Todo lo que registraste, en lista o en calendario. Si después editaste una prenda y el conjunto ya no se puede armar (dejó de cubrir una zona, las prendas ya no comparten estación o alguna no está disponible), el historial te avisa pero no lo cambia: lo que te pusiste, te lo pusiste. Desde cada registro puedes fichar sus prendas como ya mostradas o borrar el registro."
              )}
            </p>
            <p>
              {t(
                "ayuda.historial2",
                "En el calendario, cada día abre lo que te pusiste. «Repetir» registra ese mismo conjunto con la fecha de hoy. «Editar» (también en la lista) abre el registro en Armar a mano para corregir sus prendas, la fecha o la ocasión; al guardar se corrige ese mismo registro, no se crea otro."
              )}
            </p>
          </section>

          {/* ── Valija ──────────────────────────────────────────── */}
          <section id="ayuda-valija" className="ayuda-seccion">
            <h3>{titulos.valija}</h3>
            <p>
              {t(
                "ayuda.valija1",
                "Dices cuántos días de cada ocasión tiene el viaje, para qué estación, cuántos calzados como máximo y si hay algo que quieres llevar sí o sí. La app arma la lista más corta de prendas que cubre todos los días, te muestra cuánto te ahorras frente a un conjunto distinto por día, y el plan día por día. La valija queda guardada; «Nueva valija» empieza de cero."
              )}
            </p>
          </section>

          {/* ── Qué comprar ─────────────────────────────────────── */}
          <section id="ayuda-comprar" className="ayuda-seccion">
            <h3>{titulos.comprar}</h3>
            <p>
              {t(
                "ayuda.comprar1",
                "Cinco lecturas de tu armario. Si no puedes armar ningún conjunto, te dice qué te falta para cubrir el cuerpo: eso es lo primero. Las prendas que no entran en ninguna combinación, que necesitan compañera o son candidatas a irse. De qué prendas depende tu armario: las que están en más del 15% de tus conjuntos, que conviene tener repetidas. Y las gastadas, ordenadas por urgencia según cuánto las usas."
              )}
            </p>
            <p>
              {t(
                "ayuda.comprar2",
                "Y qué te conviene comprar: cada número es real. La app simula agregar esa prenda a tu armario y vuelve a contar tus conjuntos, respetando tus vetos, tus reglas de estilo y los colores que no usas. No es una opinión sobre moda."
              )}
            </p>
          </section>

          {/* ── Cargar prendas ──────────────────────────────────── */}
          <section id="ayuda-cargar" className="ayuda-seccion">
            <h3>{titulos.cargar}</h3>
            <p>
              {t(
                "ayuda.cargar1",
                "Arrastra o elige varias fotos de una vez, o toma una con la cámara del teléfono, hasta un máximo por tanda. Cada ficha se puede sacar, y «Quitar todas» vacía la tanda con unos segundos para deshacer. La app las achica en tu teléfono, las reconoce y te devuelve una ficha por foto para revisar: nombre, marca, tipo, qué es, estación, material, estado, colores y ocasiones. Si no estuvo segura te lo dice, y si algún dato no lo pudo leer en la foto lo rellena con un valor por defecto y te lo deja marcado, para que revises esos y no los doce. Si no pudo verla, la completas a mano."
              )}
            </p>
            <p>
              {t(
                "ayuda.cargar2",
                "Si una foto se parece a una prenda que ya tienes, te avisa cuál antes de guardar; no te lo impide, porque dos camisetas blancas iguales son dos prendas. Al guardar te quedas en esta pantalla con las recién guardadas, para seguir con la próxima tanda. Si cierras la app a mitad de una carga, al volver recuperas las fichas."
              )}
            </p>
            <p>
              {t(
                "ayuda.cargar3",
                "Cuando creas la cuenta tienes {dias} días de prueba, sin tarjeta, con hasta {tope} prendas para ver cómo funciona todo. Cuando la prueba termina no se borra nada: sigues viendo tu armario entero, editando las fichas, borrando lo que no uses, registrando lo que te pones y recibiendo propuestas. Lo único que necesita suscripción es agregar prendas nuevas, y con ella no hay tope.",
                { dias: DIAS_PRUEBA, tope: TOPE_PRUEBA }
              )}
            </p>
          </section>

          {/* ── Perfil ──────────────────────────────────────────── */}
          <section id="ayuda-perfil" className="ayuda-seccion">
            <h3>{titulos.perfil}</h3>
            <p>
              {t(
                "ayuda.perfil1",
                "Se abre tocando tu nombre arriba. «Cómo te sugiero la ropa» es lo que más cambia las propuestas: cuánta mezcla de colores y de formalidad aceptas, y los colores que no usas; esas prendas no aparecen en las propuestas ni se te recomienda comprarlas. Dónde vives decide la estación de hoy. Tu estilo y hasta dos acentos ordenan las propuestas sin esconder nada; con el tiempo, lo que registras va afinando lo que declaraste."
              )}
            </p>
            <p>
              {t(
                "ayuda.perfil2",
                "En «Mis listas» creas tus propias ocasiones y colores con «+ Ocasión nueva» y «+ Color nuevo», y también los puedes crear mientras editas una prenda, que además la marca ahí mismo. Ahí ves las que ya creaste y las combinaciones que marcaste como que no van; borrar una no afecta a las prendas que ya la tenían. También cambias el idioma de la app (hay doce) y ves qué versión estás usando."
              )}
            </p>
            <p>
              {t(
                "ayuda.perfil3",
                "El idioma también se elige antes de entrar, con el selector de arriba de la pantalla de acceso: los doce aparecen con su nombre en su propio idioma. La app recuerda el que elegiste en ese navegador, así que la próxima vez te recibe en el mismo. Y si te registras, tu cuenta nace en ese idioma."
              )}
            </p>
            <p>
              {t(
                "ayuda.perfil4",
                "Abajo de todo está «Tu plan»: cuál tienes, hasta cuándo, y cuántas prendas llevas del tope si lo hay. Cuando falten pocos días para que termine la prueba, o si un cobro falla, aparece además un aviso arriba de todas las pantallas; con el plan ya vencido no aparece, porque el límite ya te lo dice cuando intentas agregar una prenda."
              )}
            </p>
            <p>
              {t(
                "ayuda.perfil5",
                "Al final de todo está «Borrar mi cuenta». Borra de verdad y en el momento: tus prendas, todas tus fotos, tus outfits, tus colores y ocasiones propias, tu maleta y la cuenta misma. No queda copia y no se puede deshacer, así que te lo pregunta dos veces."
              )}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
